import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  POI_TYPES,
  POI_TYPE_LABELS,
  type PoiType,
  type PoiCreateDto,
} from '@camper-diary/shared';
import { listPois, createPoi, updatePoi, deletePoi, type Poi } from '../api/pois';
import { HttpError } from '../api/client';
import { AddressSearch } from '../components/AddressSearch';
import { reverseGeocode, type Geo } from '../api/geocoding';
import { parseCoordinate, normalizeCoordinateInput } from '../utils/coordinates';
import { openNavigation } from '../utils/navigate';
import { PoiPhotos } from '../components/PoiPhotos';
import { PoiPhotosPicker } from '../components/PoiPhotosPicker';
import { uploadPoiPhoto } from '../api/poiPhotos';

const DEFAULT_CENTER: [number, number] = [44.5, 11.3];
const DEFAULT_ZOOM = 6;

type Mode = { kind: 'idle' } | { kind: 'creating' } | { kind: 'editing'; entry: Poi };

/** Converte un codice ISO a 2 lettere ("IT") in emoji bandiera 🇮🇹. */
function countryToFlag(code: string | null): string {
  if (!code || code.length !== 2) return '🌍';
  const cc = code.toUpperCase();
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  const a = cc.charCodeAt(0);
  const b = cc.charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return '🌍';
  return String.fromCodePoint(base + a, base + b);
}

function makePoiIcon(type: PoiType): L.DivIcon {
  const { icon } = POI_TYPE_LABELS[type];
  return L.divIcon({
    className: 'cd-marker cd-marker-poi',
    html: `<div class="cd-marker-pin cd-marker-poi-pin"><span>${icon}</span></div>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -36],
  });
}

const PICK_ICON: L.DivIcon = L.divIcon({
  className: 'cd-marker cd-marker-pick',
  html: `<div class="cd-marker-pin"><span>＋</span></div>`,
  iconSize: [32, 40],
  iconAnchor: [16, 40],
});

function FitBounds({
  pois,
  pick,
}: {
  pois: Poi[];
  pick: { lat: number; lon: number } | null;
}) {
  const map = useMap();
  useEffect(() => {
    const all: [number, number][] = [
      ...pois.map((p) => [p.lat, p.lon] as [number, number]),
      ...(pick ? [[pick.lat, pick.lon] as [number, number]] : []),
    ];
    if (all.length === 0) return;
    if (all.length === 1) {
      map.setView(all[0]!, Math.max(map.getZoom(), 11));
      return;
    }
    map.fitBounds(L.latLngBounds(all), { padding: [40, 40], maxZoom: 13 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pois.map((p) => `${p.id}:${p.lat}:${p.lon}`).join('|'), pick?.lat, pick?.lon]);
  return null;
}

function ClickPicker({ onPick }: { onPick: (lat: number, lon: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export function PoisPage() {
  const [pois, setPois] = useState<Poi[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);
  const [typeFilter, setTypeFilter] = useState<PoiType | 'ALL'>('ALL');

  async function refresh() {
    try {
      setPois(await listPois());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const filteredPois = useMemo(() => {
    if (!pois) return [];
    if (typeFilter === 'ALL') return pois;
    return pois.filter((p) => p.type === typeFilter);
  }, [pois, typeFilter]);

  /** Raggruppa: country → region → poi. country=null va in fondo, region=null in cima al gruppo. */
  const groups = useMemo(() => {
    type Group = { country: string | null; regions: { region: string | null; pois: Poi[] }[] };
    const byCountry = new Map<string, Group>();
    for (const p of filteredPois) {
      const cKey = p.country ?? '__';
      if (!byCountry.has(cKey)) {
        byCountry.set(cKey, { country: p.country, regions: [] });
      }
      const g = byCountry.get(cKey)!;
      let r = g.regions.find((x) => x.region === p.region);
      if (!r) {
        r = { region: p.region, pois: [] };
        g.regions.push(r);
      }
      r.pois.push(p);
    }
    const list = [...byCountry.values()];
    list.sort((a, b) => {
      if (a.country === null) return 1;
      if (b.country === null) return -1;
      // Italia in cima per comodità
      if (a.country === 'IT' && b.country !== 'IT') return -1;
      if (b.country === 'IT' && a.country !== 'IT') return 1;
      return a.country.localeCompare(b.country);
    });
    for (const g of list) {
      g.regions.sort((a, b) => {
        if (a.region === null) return 1;
        if (b.region === null) return -1;
        return a.region.localeCompare(b.region);
      });
      for (const r of g.regions) {
        r.pois.sort((a, b) => a.name.localeCompare(b.name));
      }
    }
    return list;
  }, [filteredPois]);

  function startCreate() {
    setMode({ kind: 'creating' });
  }
  function startEdit(entry: Poi) {
    setMode({ kind: 'editing', entry });
  }
  function cancelForm() {
    setMode({ kind: 'idle' });
  }

  async function onDelete(entry: Poi) {
    if (!confirm(`Eliminare "${entry.name}" dai POI?`)) return;
    setBusy(true);
    try {
      await deletePoi(entry.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div className="title-block">
          <h1>📍 POI</h1>
          <p className="muted">
            Aree sosta, campeggi, soste libere che hai visitato e vuoi ricordare.
          </p>
        </div>
        {mode.kind === 'idle' && (
          <button className="btn btn-primary" onClick={startCreate} disabled={busy}>
            + Nuovo POI
          </button>
        )}
      </div>

      {error && <p className="err">{error}</p>}

      {mode.kind !== 'idle' && (
        <PoiForm
          initial={mode.kind === 'editing' ? mode.entry : undefined}
          onCancel={cancelForm}
          onSaved={async () => {
            cancelForm();
            await refresh();
          }}
        />
      )}

      {pois && pois.length > 0 && (
        <div className="poi-toolbar">
          <button
            className={`chip ${typeFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setTypeFilter('ALL')}
          >
            Tutti ({pois.length})
          </button>
          {POI_TYPES.map((t) => {
            const count = pois.filter((p) => p.type === t).length;
            if (count === 0) return null;
            const { label, icon } = POI_TYPE_LABELS[t];
            return (
              <button
                key={t}
                className={`chip ${typeFilter === t ? 'active' : ''}`}
                onClick={() => setTypeFilter(t)}
              >
                {icon} {label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {pois && filteredPois.length > 0 && (
        <div className="poi-map-wrap">
          <MapContainer
            center={
              filteredPois[0]
                ? [filteredPois[0].lat, filteredPois[0].lon]
                : DEFAULT_CENTER
            }
            zoom={DEFAULT_ZOOM}
            scrollWheelZoom
            style={{ height: 380, width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              maxZoom={19}
            />
            <FitBounds pois={filteredPois} pick={null} />
            {filteredPois.map((p) => (
              <Marker
                key={p.id}
                position={[p.lat, p.lon]}
                icon={makePoiIcon(p.type)}
              >
                <Popup>
                  <div className="popup">
                    <strong>{p.name}</strong>
                    <div className="muted">
                      {POI_TYPE_LABELS[p.type].icon} {POI_TYPE_LABELS[p.type].label}
                    </div>
                    {p.address && <div className="muted">{p.address}</div>}
                    <div className="coords">
                      {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      )}

      {!pois && <p className="muted">Caricamento…</p>}

      {pois && pois.length === 0 && mode.kind === 'idle' && (
        <p className="muted">
          Nessun POI salvato. Aggiungi un'area sosta, un campeggio o una sosta libera che ti è
          piaciuta.
        </p>
      )}

      {pois && filteredPois.length === 0 && pois.length > 0 && (
        <p className="muted">Nessun POI per questo filtro.</p>
      )}

      {groups.map((g) => (
        <section key={g.country ?? '__'} className="poi-country-group">
          <h2 className="poi-country-title">
            <span className="poi-flag">{countryToFlag(g.country)}</span>{' '}
            {g.country ?? 'Senza paese (riprova reverse geocoding)'}
          </h2>
          {g.regions.map((r) => (
            <div key={r.region ?? '__'} className="poi-region-group">
              <h3 className="poi-region-title">{r.region ?? '(regione non rilevata)'}</h3>
              <ul className="poi-list">
                {r.pois.map((p) => (
                  <li key={p.id} className="poi-item">
                    <div className="item-body">
                      <h4>
                        <span className="poi-type-badge" title={POI_TYPE_LABELS[p.type].label}>
                          {POI_TYPE_LABELS[p.type].icon}
                        </span>{' '}
                        {p.name}
                      </h4>
                      {p.description && <p>{p.description}</p>}
                      {p.address && <p className="muted">{p.address}</p>}
                      <p className="coords">
                        {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                      </p>
                      {p.notes && <p className="notes">📝 {p.notes}</p>}
                    </div>
                    <div className="item-actions">
                      <button
                        className="btn btn-ghost"
                        onClick={() => openNavigation(p.lat, p.lon, p.name)}
                      >
                        🧭 Naviga
                      </button>
                      <button
                        className="btn btn-ghost"
                        disabled={busy}
                        onClick={() => startEdit(p)}
                      >
                        Modifica
                      </button>
                      <button
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={() => void onDelete(p)}
                      >
                        Elimina
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function PoiForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Poi;
  onSaved: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const [type, setType] = useState<PoiType>(initial?.type ?? 'AREA_SOSTA');
  const [name, setName] = useState(initial?.name ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [lat, setLat] = useState(initial?.lat.toString() ?? '');
  const [lon, setLon] = useState(initial?.lon.toString() ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);

  function useCurrentPosition() {
    if (!('geolocation' in navigator)) {
      setLocateError('Geolocalizzazione non supportata da questo browser');
      return;
    }
    setLocateError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setLat(latitude.toFixed(6));
        setLon(longitude.toFixed(6));
        setLocating(false);
        // Reverse geocoding per riempire l'indirizzo solo se vuoto:
        // non sovrascriviamo quanto già inserito dall'utente.
        void (async () => {
          try {
            const r = await reverseGeocode(latitude, longitude);
            if (r) setAddress((cur) => (cur.trim() === '' ? r.displayName : cur));
          } catch {
            // silenzioso: il reverse è best-effort
          }
        })();
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          setLocateError('Permesso di geolocalizzazione negato dal browser');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setLocateError('Posizione non disponibile (GPS spento o segnale assente)');
        } else if (err.code === err.TIMEOUT) {
          setLocateError('Timeout: nessun fix GPS entro 15 secondi');
        } else {
          setLocateError('Errore di geolocalizzazione');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }

  function onAddressPick(r: Geo) {
    setLat(r.lat.toFixed(6));
    setLon(r.lon.toFixed(6));
    setAddress(r.displayName);
  }

  function onMapClick(lat: number, lon: number) {
    setLat(lat.toFixed(6));
    setLon(lon.toFixed(6));
  }

  const previewLat = parseCoordinate(lat);
  const previewLon = parseCoordinate(lon);
  const pick =
    previewLat !== null &&
    previewLon !== null &&
    previewLat >= -90 &&
    previewLat <= 90 &&
    previewLon >= -180 &&
    previewLon <= 180
      ? { lat: previewLat, lon: previewLon }
      : null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setUploadErrors([]);
    const latNum = parseCoordinate(lat);
    const lonNum = parseCoordinate(lon);
    if (latNum === null || latNum < -90 || latNum > 90) {
      setError('Latitudine non valida (tra -90 e 90)');
      return;
    }
    if (lonNum === null || lonNum < -180 || lonNum > 180) {
      setError('Longitudine non valida (tra -180 e 180)');
      return;
    }
    setSubmitting(true);
    const payload: PoiCreateDto = {
      type,
      name: name.trim(),
      description: description.trim() || null,
      notes: notes.trim() || null,
      lat: latNum,
      lon: lonNum,
      address: address.trim() || null,
    };

    let saved: Poi;
    try {
      saved = initial ? await updatePoi(initial.id, payload) : await createPoi(payload);
    } catch (err) {
      setError(err instanceof HttpError ? err.apiError.message : 'Errore di connessione');
      setSubmitting(false);
      return;
    }

    // upload coda foto (solo in creazione; in edit gli upload sono già stati
    // fatti immediatamente da PoiPhotos)
    const errors: string[] = [];
    if (!initial && pendingFiles.length > 0) {
      for (let i = 0; i < pendingFiles.length; i++) {
        setUploadingIdx(i);
        const f = pendingFiles[i]!;
        try {
          await uploadPoiPhoto(saved.id, f);
        } catch (err) {
          const msg =
            err instanceof HttpError
              ? err.apiError.message
              : err instanceof Error
                ? err.message
                : 'Errore upload';
          errors.push(`${f.name}: ${msg}`);
        }
      }
      setUploadingIdx(null);
    }

    if (errors.length > 0) {
      setUploadErrors(errors);
      setSubmitting(false);
      // POI già creato, ma alcune foto sono fallite: lasciamo il form aperto
      // per dare visibilità degli errori. L'utente può chiudere a mano.
      return;
    }

    await onSaved();
  }

  return (
    <form className="form-card" onSubmit={handleSubmit}>
      <div className="row">
        <label>
          <span>Tipo *</span>
          <select value={type} onChange={(e) => setType(e.target.value as PoiType)} required>
            {POI_TYPES.map((t) => (
              <option key={t} value={t}>
                {POI_TYPE_LABELS[t].icon} {POI_TYPE_LABELS[t].label}
              </option>
            ))}
          </select>
        </label>
        <label className="grow">
          <span>Nome *</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            maxLength={200}
          />
        </label>
      </div>

      <label>
        <span>Cerca indirizzo</span>
        <AddressSearch value={address} onChange={setAddress} onPick={onAddressPick} />
      </label>

      <div className="poi-here-row">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={useCurrentPosition}
          disabled={locating}
        >
          {locating ? '📡 Localizzazione…' : '📍 Sono qui'}
        </button>
        {locateError && <span className="err inline">{locateError}</span>}
      </div>

      <div className="map-wrap picking" style={{ height: 300 }}>
        <MapContainer
          center={pick ? [pick.lat, pick.lon] : DEFAULT_CENTER}
          zoom={pick ? 13 : DEFAULT_ZOOM}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            maxZoom={19}
          />
          <ClickPicker onPick={onMapClick} />
          {pick && <Marker position={[pick.lat, pick.lon]} icon={PICK_ICON} />}
        </MapContainer>
        <div className="map-hint">📍 Clicca sulla mappa per scegliere la posizione</div>
      </div>

      <div className="row">
        <label>
          <span>Latitudine *</span>
          <input
            type="text"
            inputMode="decimal"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            onBlur={(e) => setLat(normalizeCoordinateInput(e.target.value))}
            required
          />
        </label>
        <label>
          <span>Longitudine *</span>
          <input
            type="text"
            inputMode="decimal"
            value={lon}
            onChange={(e) => setLon(e.target.value)}
            onBlur={(e) => setLon(normalizeCoordinateInput(e.target.value))}
            required
          />
        </label>
      </div>

      <label>
        <span>Descrizione</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={5000}
          placeholder="Cos'è questo posto: tipo di struttura, servizi, accesso…"
        />
      </label>

      <label>
        <span>Note personali</span>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={5000}
          placeholder="La tua opinione, perché ne vale la pena, dettagli pratici (prezzo, periodo migliore, ecc.)"
        />
      </label>

      {error && <p className="err">{error}</p>}

      <div className="poi-photos-section">
        <h3 className="poi-photos-title">Foto</h3>
        {initial ? (
          <PoiPhotos poiId={initial.id} />
        ) : (
          <PoiPhotosPicker files={pendingFiles} onChange={setPendingFiles} />
        )}
        {uploadingIdx !== null && (
          <p className="muted small">
            ⏳ Caricamento foto {uploadingIdx + 1} / {pendingFiles.length}…
          </p>
        )}
        {uploadErrors.length > 0 && (
          <ul className="upload-errors">
            {uploadErrors.map((m, i) => (
              <li key={i} className="err">{m}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting
            ? uploadingIdx !== null
              ? 'Caricamento foto…'
              : 'Salvataggio…'
            : initial
              ? 'Salva modifiche'
              : 'Crea POI'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
          Annulla
        </button>
      </div>
    </form>
  );
}
